/**
 * This module exports functions useful for investigating performance.
 */

import log from 'common/ScalyrLogger';
import { isEqual } from 'lodash';

/**
 * Deep compares two objects and returns true if they are equal. Logs differences if requested.
 *
 * @param obj1        One object (e.g., a bundle of properties)
 * @param obj2        Another object
 * @param doLog       true to compare each property and log changes
 * @param verboseLog  true to log changes to individual properties
 * @param logLabel    Identifying text to output (e.g., 'MyComponent')
 *
 * @return true if they're the same, false otherwise

  Example usage:

const currentState = {
  selectedPlotIndex,
  saveGraphToDashboard,
  ...,
};
const prevState = usePrevious(currentState as unknown as Record<string, unknown>);
areEqual(prevState, currentState as unknown as Record<string, unknown>, true, true, 'MyComponent state');

*/

export const areEqual = (
  obj1: Record<string, unknown> | undefined,
  obj2: Record<string, unknown>,
  doLog = false,
  verboseLog = false,
  logLabel = 'MyComponent',
): boolean => {
  const allowDebugOutput = false; // Master control to keep the output down -- set to true when debugging performance
  if (doLog && allowDebugOutput) {
    if (obj1) {
      const allKeys = Object.keys(obj2);
      const prevValues = Object.values(obj1);
      const nextValues = Object.values(obj2);
      allKeys.forEach((key, index) => {
        if (!isEqual(prevValues[index], nextValues[index])) {
          if (verboseLog) {
            log.debug(`${logLabel} - CHANGED`, key, prevValues[index], nextValues[index]);
          } else {
            log.debug(`${logLabel} prop: ${key} - CHANGED`);
          }
        }
      });
    } else {
      log.debug(`${logLabel} - NEW`, obj2);
    }
  }
  return isEqual(obj1, obj2);
};
